import { SORT_OPTIONS } from '../constants/sortOptions';
import { findBrandTermIdFromSummary } from './brandUtils';
import { type FilterOptions, type FilterSummary } from '../types/filters';
import { findNameByTermId, findTermIdBySlug, type TaxonomyCategory } from './categoryUtils';
import type { CollectionTerm } from '@skin-seoul/service';
import { decodeHTMLEntities } from '@skin-seoul/utils';

const getCategoryTitleFromFilters = (activeFilters: FilterOptions, categories: TaxonomyCategory[]): string | null => {
  if (!activeFilters.categories?.length) {
    return null;
  }

  const categoryTermId = activeFilters.categories[0];
  return findNameByTermId(categories, categoryTermId) || null;
};

const getSubcategoryTitle = (subcategory: string, categories: TaxonomyCategory[]): string | null => {
  const subcategoryTermId = findTermIdBySlug(categories, subcategory);
  if (!subcategoryTermId) {
    return null;
  }

  return findNameByTermId(categories, subcategoryTermId) || null;
};

const getCategoryTitle = (category: string, categories: TaxonomyCategory[]): string | null => {
  const categoryTermId = findTermIdBySlug(categories, category);
  if (!categoryTermId) {
    return null;
  }

  return findNameByTermId(categories, categoryTermId) || null;
};

const generateCategoryTitle = (
  category?: string,
  subcategory?: string,
  activeFilters?: FilterOptions,
  categories?: TaxonomyCategory[],
): string | null => {
  if (!categories) {
    return null;
  }

  if (activeFilters) {
    const filterTitle = getCategoryTitleFromFilters(activeFilters, categories);
    if (filterTitle) {
      return filterTitle;
    }
  }

  if (subcategory) {
    const subcategoryTitle = getSubcategoryTitle(subcategory, categories);
    if (subcategoryTitle) {
      return subcategoryTitle;
    }
  }

  if (category) {
    const categoryTitle = getCategoryTitle(category, categories);
    if (categoryTitle) {
      return categoryTitle;
    }
  }

  return null;
};

const generateProductTagTitle = (productTag: string): string => {
  return productTag
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getBrandNameFromSummary = (brandTermId: number, filterSummary: FilterSummary): string | null => {
  const brandItem = filterSummary.brand?.find((item) => item.termId === brandTermId);
  return brandItem ? decodeHTMLEntities(brandItem.name) : null;
};

const generateBrandTitle = (
  brand?: string,
  activeFilters?: FilterOptions,
  filterSummary?: FilterSummary,
): string | null => {
  if (!filterSummary) {
    return null;
  }

  if (brand) {
    const brandTermId = findBrandTermIdFromSummary(brand, filterSummary);
    if (brandTermId) {
      const brandName = getBrandNameFromSummary(brandTermId, filterSummary);
      if (brandName) {
        return brandName;
      }
    }
  }

  if (activeFilters?.brands?.length) {
    const brandTermId = activeFilters.brands[0];
    const brandName = getBrandNameFromSummary(brandTermId, filterSummary);
    if (brandName) {
      return brandName;
    }
  }

  return null;
};

interface GeneratePageTitleParams {
  category?: string;
  subcategory?: string;
  brand?: string;
  productTag?: string;
  filterSummary?: FilterSummary;
  activeFilters?: FilterOptions;
  sortBy?: string;
  categories?: TaxonomyCategory[];
}

export const generatePageTitle = (params: GeneratePageTitleParams): string => {
  const { category, subcategory, brand, productTag, filterSummary, activeFilters, categories } = params;
  const defaultTitle = 'Shop';

  const categoryTitle = generateCategoryTitle(category, subcategory, activeFilters, categories);
  if (categoryTitle) {
    return categoryTitle;
  }

  if (productTag) {
    return generateProductTagTitle(productTag);
  }

  const brandTitle = generateBrandTitle(brand, activeFilters, filterSummary);
  if (brandTitle) {
    return brandTitle;
  }

  return defaultTitle;
};

export const generateSearchPageTitle = (searchQuery: string): string => {
  return `Search Results For: ${searchQuery}`;
};

export const getSortLabel = (sortBy?: string): string => {
  if (!sortBy) {
    return SORT_OPTIONS[0].label;
  }

  const sortOption = SORT_OPTIONS.find((option) => option.value === sortBy);
  return sortOption?.label || 'Best Seller';
};

/**
 * Prioritizes: searchQuery > collectionTerm.name > generated title
 */
export const getPageTitle = (params: {
  searchQuery?: string;
  collectionTerm?: CollectionTerm;
  category?: string;
  subcategory?: string;
  brand?: string;
  productTag?: string;
  filterSummary?: FilterSummary;
  filters?: FilterOptions;
  sortBy?: string;
}): string => {
  const { searchQuery, collectionTerm, category, subcategory, brand, productTag, filterSummary, filters, sortBy } =
    params;

  if (searchQuery) {
    return generateSearchPageTitle(searchQuery);
  }

  if (collectionTerm?.name) {
    return decodeHTMLEntities(collectionTerm.name);
  }

  return generatePageTitle({
    category,
    subcategory,
    brand,
    productTag,
    filterSummary,
    activeFilters: filters || {},
    sortBy,
  });
};
